import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatBRL } from "../../lib/api";
import {
  TrendingUp, Users, Receipt, Calendar, AlertTriangle, Target, DollarSign, Activity,
  Building2, Clock, Layers
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";

const CHART_COLORS = ["#14213D", "#D4AF37", "#1F335E", "#7a6519", "#F3DFA2", "#0A1128"];

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  const [ops, setOps] = useState(null);
  const [hb, setHb] = useState(null);
  const [queue, setQueue] = useState(null);
  const nav = useNavigate();
  // RC17 — cards viram atalhos de navegação (rotas existentes; sem novos dados)
  const CLICK_CLS = "cursor-pointer hover:shadow-md hover:border-[#D4AF37]/60 transition-all";

  useEffect(() => {
    Promise.all([
      api.get("/reports/kpis").then(r => setKpis(r.data)),
      api.get("/reports/revenue-series").then(r => setSeries(r.data)),
      api.get("/reports/ops-kpis").then(r => setOps(r.data)).catch(() => setOps(null)),
      api.get("/admin/hour-bank/kpis").then(r => setHb(r.data)).catch(() => setHb(null)),
      api.get("/admin/checkout-queue").then(r => setQueue(r.data)).catch(() => setQueue(null)),
    ]).catch(() => {});
  }, []);

  if (!kpis) return <div className="text-gray-500">Carregando…</div>;

  const cards = [
    { label: "MRR", value: formatBRL(kpis.mrr), icon: TrendingUp, accent: true, hint: "Receita recorrente mensal", to: "/admin/financeiro" },
    { label: "Receita do Mês", value: formatBRL(kpis.revenue_month), icon: DollarSign, hint: "Faturas pagas neste mês", to: "/admin/financeiro" },
    { label: "Clientes Ativos", value: kpis.active_clients, icon: Users, hint: `de ${kpis.total_clients} cadastrados`, to: "/admin/clientes" },
    { label: "Ticket Médio", value: formatBRL(kpis.ticket_avg), icon: Receipt, hint: "Por fatura paga", to: "/admin/financeiro" },
    { label: "Reservas (mês)", value: kpis.reservations_month, icon: Calendar, hint: "Confirmadas + concluídas", to: "/admin/reservas" },
    { label: "Conversão Leads", value: `${kpis.conversion_rate}%`, icon: Target, hint: `${kpis.won_leads} de ${kpis.total_leads}`, to: "/admin/leads" },
    { label: "Faturas Pagas", value: kpis.paid_invoices, icon: Activity, hint: "Total no histórico", to: "/admin/financeiro" },
    { label: "Inadimplência", value: kpis.overdue_invoices, icon: AlertTriangle, hint: "Faturas vencidas", warning: true, to: "/admin/financeiro" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Dashboard Executivo</div>
        <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Visão geral do coworking</h1>
      </div>

      {/* RC19 — Fila de Check-outs: em utilização há mais tempo que o contratado */}
      {queue && queue.count > 0 && (
        <div className="mb-8" data-testid="dashboard-checkout-queue">
          <div className="text-[11px] uppercase tracking-[0.18em] text-red-600 font-bold mb-3">
            Fila de Check-outs — tempo contratado excedido ({queue.count})
          </div>
          <div className="bg-white border border-red-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
            {queue.items.map((q) => (
              <div
                key={q.id}
                onClick={() => nav("/admin/reservas")}
                role="link" title="Ir para Reservas"
                className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-red-50/60 transition-colors"
                data-testid={`checkout-queue-item-${q.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-8 h-8 rounded-md bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle size={15} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#14213D] truncate">
                      {q.space_name} · {q.client_name || "—"}
                      {q.is_hour_bank && <span className="ml-2 text-[9px] uppercase font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Banco de Horas</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      Check-in {new Date(q.checked_in_at).toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" })}
                      {" · "}contratado {q.contracted_hours}h · em uso {q.elapsed_hours}h
                    </div>
                  </div>
                </div>
                <span className="text-xs font-bold text-red-600 whitespace-nowrap" data-testid={`queue-overby-${q.id}`}>
                  +{q.over_by_hours}h além
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RC6 — Banco de Horas */}
      {hb && (
        <div className="mb-8" data-testid="dashboard-hour-bank">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mb-3">Banco de Horas</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "packages_sold", label: "Bancos vendidos", icon: Layers, testid: "hb-sold" },
              { k: "hours_sold", label: "Horas vendidas", icon: Clock, suffix: "h", testid: "hb-hours-sold" },
              { k: "hours_consumed", label: "Horas consumidas", icon: Activity, suffix: "h", testid: "hb-consumed" },
              { k: "hours_available", label: "Horas disponíveis", icon: TrendingUp, suffix: "h", accent: true, testid: "hb-available" },
              { k: "active_packages", label: "Bancos ativos", icon: Building2, testid: "hb-active" },
              { k: "expiring_7d", label: "Vencendo em 7 dias", icon: Calendar, warning: true, testid: "hb-expiring" },
              { k: "low_balance_clients", label: "Clientes com < 4h", icon: AlertTriangle, warning: true, testid: "hb-low" },
            ].map((c) => (
              <div key={c.k} onClick={() => nav("/admin/clientes")} title="Ver detalhes" role="link"
                className={`kpi-card ${CLICK_CLS} ${c.accent ? "border-[#D4AF37] bg-gradient-to-br from-white to-[#D4AF37]/5" : ""}`} data-testid={c.testid}>
                <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${
                  c.warning && (hb[c.k] || 0) > 0 ? "bg-red-50 text-red-600" :
                  c.accent ? "bg-[#D4AF37]/20 text-[#7a6519]" : "bg-[#14213D]/5 text-[#14213D]"
                }`}>
                  <c.icon size={16} />
                </div>
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{c.label}</div>
                <div className="font-display text-2xl text-[#14213D] font-medium">{hb[c.k] ?? 0}{c.suffix || ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Operacional hoje — dados de reservas/pagamentos existentes */}
      {ops && (
        <div className="mb-8" data-testid="dashboard-operational">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mb-3">Operacional hoje</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`kpi-card ${CLICK_CLS}`} onClick={() => nav("/admin/reservas")} title="Ver detalhes" role="link" data-testid="ops-reservations-today">
              <div className="w-9 h-9 rounded-md flex items-center justify-center bg-[#14213D]/5 text-[#14213D] mb-3">
                <Calendar size={16} />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Reservas de hoje</div>
              <div className="font-display text-2xl text-[#14213D] font-medium">{ops.reservations_today ?? 0}</div>
              <div className="text-xs text-gray-400 mt-1">Confirmadas + pendentes</div>
            </div>
            <div className={`kpi-card ${CLICK_CLS} border-[#D4AF37] bg-gradient-to-br from-white to-[#D4AF37]/5`} onClick={() => nav("/admin/financeiro")} title="Ver detalhes" role="link" data-testid="ops-revenue-today">
              <div className="w-9 h-9 rounded-md flex items-center justify-center bg-[#D4AF37]/20 text-[#7a6519] mb-3">
                <DollarSign size={16} />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Receita do dia</div>
              <div className="font-display text-2xl text-[#14213D] font-medium">{formatBRL(ops.revenue_today ?? 0)}</div>
              <div className="text-xs text-gray-400 mt-1">Faturas pagas hoje</div>
            </div>
            <div className={`kpi-card ${CLICK_CLS}`} onClick={() => nav("/admin/financeiro")} title="Ver detalhes" role="link" data-testid="ops-revenue-month">
              <div className="w-9 h-9 rounded-md flex items-center justify-center bg-[#14213D]/5 text-[#14213D] mb-3">
                <TrendingUp size={16} />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Receita do mês</div>
              <div className="font-display text-2xl text-[#14213D] font-medium">{formatBRL(ops.revenue_month ?? 0)}</div>
              <div className="text-xs text-gray-400 mt-1">Acumulado do mês</div>
            </div>
            <div className={`kpi-card ${CLICK_CLS}`} onClick={() => nav("/admin/financeiro")} title="Ver detalhes" role="link" data-testid="ops-pending-payments">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${(ops.pending_payments_count || 0) > 0 ? "bg-red-50 text-red-600" : "bg-[#14213D]/5 text-[#14213D]"}`}>
                <AlertTriangle size={16} />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Pagamentos pendentes</div>
              <div className="font-display text-2xl text-[#14213D] font-medium">{ops.pending_payments_count ?? 0}</div>
              <div className="text-xs text-gray-400 mt-1">Total: {formatBRL(ops.pending_payments_total ?? 0)}</div>
            </div>
          </div>

          {/* Próximas reservas + Ocupação por sala */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="ops-upcoming-reservations">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-3">Próximas reservas confirmadas</div>
              {(ops.upcoming_reservations || []).length === 0 ? (
                <div className="text-xs text-gray-400 py-3">Nenhuma reserva próxima.</div>
              ) : (
                <div className="space-y-2">
                  {ops.upcoming_reservations.map((r) => {
                    const dt = new Date(r.start_time);
                    return (
                      <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-0">
                        <div>
                          <div className="font-semibold text-[#14213D]">{r.space_name}</div>
                          <div className="text-xs text-gray-500">{r.guest_name || r.client_name || "—"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-600">{dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}</div>
                          <div className="text-[11px] text-gray-400">{dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="ops-space-occupancy">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-3">Ocupação das salas (mês)</div>
              {(ops.occupancy || []).length === 0 ? (
                <div className="text-xs text-gray-400 py-3">Sem dados de ocupação.</div>
              ) : (
                <div className="space-y-2.5">
                  {(ops.occupancy || []).slice(0, 6).map((o) => (
                    <div key={o.space_id} data-testid={`ops-occupancy-${o.space_id}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-[#14213D] font-medium truncate">{o.space_name}</span>
                        <span className="text-gray-500 font-semibold">{o.occupancy_pct}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#14213D] to-[#D4AF37]"
                          style={{ width: `${Math.min(100, o.occupancy_pct)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} onClick={() => c.to && nav(c.to)} title={c.to ? "Ver detalhes" : undefined} role={c.to ? "link" : undefined}
            className={`kpi-card ${c.to ? CLICK_CLS : ""} ${c.accent ? "border-[#D4AF37] bg-gradient-to-br from-white to-[#D4AF37]/5" : ""}`} data-testid={`kpi-${c.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${c.warning ? "bg-red-50 text-red-600" : c.accent ? "bg-[#D4AF37]/20 text-[#7a6519]" : "bg-[#14213D]/5 text-[#14213D]"}`}>
                <c.icon size={16} />
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{c.label}</div>
            <div className="font-display text-2xl text-[#14213D] font-medium">{c.value}</div>
            <div className="text-xs text-gray-400 mt-1">{c.hint}</div>
          </div>
        ))}
      </div>

      {/* Receita / faturas tradicionais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Receita — últimos 6 meses</div>
              <div className="font-display text-xl text-[#14213D]">Tendência</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={series}>
              <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
              <Line type="monotone" dataKey="revenue" stroke="#14213D" strokeWidth={2.5} dot={{ fill: "#D4AF37", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Comparativo</div>
          <div className="font-display text-xl text-[#14213D] mb-4">Faturas (status atual)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={[
              { name: "Pagas", value: kpis.paid_invoices },
              { name: "Pendentes", value: kpis.total_invoices - kpis.paid_invoices - kpis.overdue_invoices },
              { name: "Vencidas", value: kpis.overdue_invoices },
            ]}>
              <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
              <Bar dataKey="value" fill="#14213D" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Operação — Novos KPIs Piabetá */}
      {ops && (
        <>
          <div className="mb-4 mt-2">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Operação · Piabetá</div>
            <h2 className="font-display text-xl text-[#14213D]">Indicadores operacionais</h2>
          </div>

          {/* Cards de horas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="ops-hours-cards">
            <div className="kpi-card">
              <Clock size={16} className="text-[#D4AF37] mb-3" />
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Horas vendidas</div>
              <div className="font-display text-2xl text-[#14213D]">{ops.hours.sold}h</div>
              <div className="text-xs text-gray-400 mt-1">Em pacotes ativos + histórico</div>
            </div>
            <div className="kpi-card">
              <Activity size={16} className="text-[#D4AF37] mb-3" />
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Horas utilizadas</div>
              <div className="font-display text-2xl text-[#14213D]">{ops.hours.used}h</div>
              <div className="text-xs text-gray-400 mt-1">Consumidas</div>
            </div>
            <div className="kpi-card">
              <TrendingUp size={16} className="text-emerald-500 mb-3" />
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Horas restantes</div>
              <div className="font-display text-2xl text-emerald-700">{ops.hours.remaining}h</div>
              <div className="text-xs text-gray-400 mt-1">A consumir</div>
            </div>
            <div className="kpi-card">
              <Layers size={16} className="text-[#D4AF37] mb-3" />
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Pacotes ativos</div>
              <div className="font-display text-2xl text-[#14213D]">{ops.hours.active_packages}</div>
              <div className="text-xs text-gray-400 mt-1">de {ops.hours.total_packages} totais</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Ocupação por sala */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6" data-testid="occupancy-card">
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={14} className="text-[#D4AF37]" />
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Ocupação por sala (mês)</div>
              </div>
              <div className="font-display text-xl text-[#14213D] mb-4">Top 6 espaços</div>
              {ops.occupancy.length === 0 ? (
                <div className="text-gray-400 text-sm py-8 text-center">Sem reservas neste mês</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={ops.occupancy.slice(0, 6)} layout="vertical">
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                    <XAxis type="number" stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="space_name" stroke="#9CA3AF" fontSize={10} width={120} />
                    <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
                    <Bar dataKey="occupancy_pct" fill="#D4AF37" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Receita por categoria */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6" data-testid="revenue-by-category-card">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Receita por categoria (mês)</div>
              <div className="font-display text-xl text-[#14213D] mb-4">Distribuição</div>
              {ops.revenue_by_category.length === 0 ? (
                <div className="text-gray-400 text-sm py-8 text-center">Sem receita registrada neste mês</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={ops.revenue_by_category}
                      dataKey="amount" nameKey="category"
                      innerRadius={50} outerRadius={90}
                      paddingAngle={2}
                    >
                      {ops.revenue_by_category.map((entry, idx) => (
                        <Cell key={entry.category || idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Clientes ativos por plano */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-8" data-testid="clients-by-plan-card">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Clientes ativos por plano</div>
            <div className="font-display text-xl text-[#14213D] mb-4">Distribuição de contratos</div>
            {ops.clients_by_plan.length === 0 ? (
              <div className="text-gray-400 text-sm py-8 text-center">Sem contratos ativos</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ops.clients_by_plan.map((row) => (
                  <div key={row.plan} className="bg-gray-50 border border-gray-200 rounded px-4 py-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-[#14213D] truncate pr-2">{row.plan}</div>
                    <div className="font-display text-xl text-[#D4AF37] flex-shrink-0">{row.clients}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
